import { defineStore } from 'pinia'
import { useSystemStore } from './system'
import api from '@/api'
import Pusher from 'pusher-js'
import Echo from 'laravel-echo'
import { useAuthStore } from './auth'

export const useNotificationStore = defineStore('notification', {
  state: () => ({
    alerts: [],
    notifications: [],
    downloads: [],
    displayedAlert: null,
    displayedNotification: null,
    notificationTimer: null,
    notificationAutoCloseTimer: null,
    notificationSound: new Audio('/notification.mp3')
  }),
  actions: {
    initLaravelEcho() {
      window.Pusher = Pusher

      window.Echo = new Echo({
        broadcaster: 'reverb',
        key: import.meta.env.VITE_REVERB_APP_KEY,
        wsHost: import.meta.env.VITE_REVERB_HOST,
        wsPort: import.meta.env.VITE_REVERB_PORT,
        wssPort: import.meta.env.VITE_REVERB_PORT,
        forceTLS: (import.meta.env.VITE_REVERB_SCHEME ?? 'https') === 'https',
        enabledTransports: ['ws', 'wss'],
        authEndpoint: `${import.meta.env.VITE_API_HOST}/broadcasting/auth`,
        bearerToken: localStorage.accessToken
      })

      const systemStore = useSystemStore()
      const authStore = useAuthStore()
      window.Echo.private(`EcosystemApp.${systemStore.hub.ecosystem_app.id}.User.${authStore.authUser.id}`)
        .listen('.NotificationAdd', (e) => {
          this.addNotification(e)
        })
        .listen('.NotificationRead', (e) => {
          this.markNotificationAsReadRemote(e.id, e.read_at)
        })
        .listen('.NotificationRemove', (e) => {
          this.deleteNotificationRemote(e.id)
        })
        .listen('.AlertAdd', (e) => {
          this.addAlert(e.data)
        })
        .listen('.DownloadAdd', (e) => {
          this.addDownload(e.data)
        })
        .listen('.DownloadRemove', (e) => {
          this.removeDownload(e.data.id)
        })

      window.Echo.connector.pusher.connection.bind('connected', () => {
        this.getNotifications()
        this.getDownloads()
      })
    },
    async getAlerts() {
      const systemStore = useSystemStore()
      const authStore = useAuthStore()
      const response = await api.get(`v3/members-hub-alerts`, {
        params: {
          members_hub_id: systemStore.hub.id,
          country: authStore.selectedOffice?.country || 'GB',
          is_published: true
        }
      })
      this.alerts = response.data.data
      this.displayNextAlert()
    },
    addAlert(data) {
      let alert = this.alerts.find((a) => a.id === data.id)
      if (alert) {
        alert = data
      } else {
        this.alerts.unshift(data)
      }
      this.dismissAlert()
    },
    displayNextAlert(id) {
      const nextAlert = this.alerts.find((a) => (id ? a.id === id : a.should_notify_users && !a.is_read)) || null
      if (!nextAlert) {
        return
      }

      const authStore = useAuthStore()

      // A manual click from the dashboard passes an id and should always open the
      // alert. Do not disturb only suppresses the alert auto-displaying on publish.
      if (!id && authStore.authUser.do_not_disturb) {
        return
      }

      if (!authStore.authUser.mute_notifications && !id) {
        this.notificationSound.play().catch(() => console.warn('Notification sound muted until user interaction.'))
      }
      this.displayedAlert = nextAlert
      if (!this.displayedAlert.is_read) {
        this.markAlertAsRead(this.displayedAlert.id)
      }
    },
    dismissAlert() {
      this.displayedAlert = null

      setTimeout(() => {
        this.displayNextAlert()
      }, 1000)
    },
    async markAlertAsRead(id) {
      let alert = this.alerts.find((n) => n.id === id && !n.is_read)
      if (!alert) {
        return
      }

      alert.is_read = true
      await api.post(`v3/members-hub-alerts/${this.displayedAlert.id}/read`)
    },
    async getNotifications() {
      // Refreshed every time the socket reconnects, which is when the connection is at its
      // least reliable. A refresh that fails keeps the list already on screen.
      try {
        const systemStore = useSystemStore()
        const response = await api.get(`v3/notifications`, {
          params: {
            ecosystem_app_id: systemStore.hub.ecosystem_app.id
          }
        })
        this.notifications = response.data
      } catch (e) {
        console.warn('Notifications could not be refreshed.', e)
      }
    },
    addNotification(data) {
      const authStore = useAuthStore()
      this.notifications.unshift(data)

      clearTimeout(this.notificationTimer)
      this.dismissNotification()
      if (!authStore.authUser.do_not_disturb) {
        if (!authStore.authUser.mute_notifications) {
          this.notificationSound.play().catch(() => console.warn('Notification sound muted until user interaction.'))
        }
        this.notificationTimer = setTimeout(() => {
          this.displayedNotification = data

          this.startAutoHideTimer()
        }, 300)
      }
    },
    dismissNotification() {
      this.stopAutoHideTimer()
      this.displayedNotification = null
    },
    async deleteNotification(id) {
      this.deleteNotificationRemote(id)
      await api.delete(`v3/notifications/${id}`)
    },
    deleteNotificationRemote(id) {
      const notification = this.notifications.find((n) => n.id === id)
      if (!notification) {
        return
      }

      this.notifications.splice(this.notifications.indexOf(notification), 1)
    },
    async markNotificationAsRead(id) {
      let notification = this.notifications.find((n) => n.id === id)
      if (!notification) {
        return
      }

      const response = await api.post(`v3/notifications/${id}/read`)
      notification.read_at = response.data.read_at
    },
    markNotificationAsReadRemote(id, timestamp) {
      let notification = this.notifications.find((n) => n.id === id)
      if (!notification) {
        return
      }

      notification.read_at = timestamp
    },
    startAutoHideTimer() {
      if (!this.displayedNotification) {
        return
      }

      this.notificationAutoCloseTimer = setTimeout(() => {
        this.dismissNotification()
      }, 10000)
    },
    stopAutoHideTimer() {
      clearTimeout(this.notificationAutoCloseTimer)
    },
    async getDownloads() {
      try {
        const systemStore = useSystemStore()
        const response = await api.get('v3/downloads', {
          params: {
            ecosystem_app_id: systemStore.hub.ecosystem_app.id
          }
        })
        this.downloads = response.data
      } catch (e) {
        console.log(e)
      }
    },
    addDownload(data) {
      this.removeDownload(data.id)
      this.downloads.unshift(data)
    },
    removeDownload(id) {
      let download = this.downloads.find((d) => d.id === id)
      if (download) {
        this.downloads.splice(this.downloads.indexOf(download), 1)
      }
    },
    async deleteDownload(id) {
      this.removeDownload(id)
      await api.delete(`v3/downloads/${id}`)
    }
  }
})
